import React, { useState } from "react";
import PropTypes from "prop-types";
import { object, string } from "yup";
import { useForm } from "react-hook-form";
import {
  Box,
  Button,
  TextField,
  Typography,
  Grid,
  useTheme,
  InputAdornment
} from "@mui/material";
import {
  Mail,
  Send
} from 'lucide-react';
import { emailReceipt } from "./ReceiptService";
import { yupResolver } from "@hookform/resolvers/yup";
import { useLoadingSpinner } from "../../utils/components/LoadingSpinnerProvider";
import { tokens } from "../../theme";

const receiptEmailSchema = object().shape({
  address: string()
    .email("Invalid email address")
    .required("Email address is required"),
  subject: string().required("Subject is required"),
  body: string().required("Body is required"),
});

const ReceiptEmailingForm = (props) => {
  const { receiptId, emailAddress, toggleModal } = props;
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const setIsLoading = useLoadingSpinner();
  const [apiError, setApiError] = useState(false);

  const {
    clearErrors,
    handleSubmit,
    register,
    reset,
    formState: { errors },
  } = useForm({
    resolver: yupResolver(receiptEmailSchema),
    defaultValues: {
      address: emailAddress,
      subject: `Official Receipt #${receiptId} - Le Prestige Hall`,
      body: `Dear Valued Guest,\n\nPlease find your official digital receipt for your recent transaction with Le Prestige Hall attached below.\n\nThank you for choosing us for your special occasion.`,
    },
  });

  const handleCancel = () => {
    clearErrors();
    reset();
    toggleModal !== undefined && toggleModal(false);
  };

  const onSubmit = async (data) => {
    setIsLoading(true);
    await emailReceipt(receiptId, data, setApiError, setIsLoading);
    if (!apiError) {
      toggleModal(false);
    }
  };

  return (
    <Box
      sx={{
        bgcolor: theme.palette.mode === "dark" ? colors.primary[500] : "#FFFFFF",
        p: { xs: 3, md: 5 },
        borderRadius: "24px",
      }}
    >
      <form onSubmit={handleSubmit(onSubmit)}>
        <Grid container spacing={3}>
          <Grid item xs={12}>
            <Box display="flex" alignItems="center" gap={1.5} mb={1}>
              <Mail size={28} color={colors.greenAccent[500]} />
              <Typography variant="h3" fontWeight="700" sx={{ color: colors.grey[100], fontFamily: "Josefin Sans" }}>
                Digital Dispatch
              </Typography>
            </Box>
            <Typography variant="body1" sx={{ color: colors.grey[300], mb: 2 }}>
              Distribute the official digital receipt to the specified recipient.
            </Typography>
          </Grid>

          <Grid item xs={12}>
            <TextField
              fullWidth
              label="Recipient Address"
              {...register("address")}
              error={!!errors.address}
              helperText={errors.address?.message}
              variant="outlined"
              sx={{ "& .MuiOutlinedInput-root": { borderRadius: "12px" } }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Mail size={18} color={colors.grey[400]} />
                  </InputAdornment>
                ),
              }}
            />
          </Grid>

          <Grid item xs={12}>
            <TextField
              fullWidth
              label="Subject Line"
              {...register("subject")}
              error={!!errors.subject}
              helperText={errors.subject?.message}
              variant="outlined"
              sx={{ "& .MuiOutlinedInput-root": { borderRadius: "12px" } }}
            />
          </Grid>

          <Grid item xs={12}>
            <TextField
              fullWidth
              multiline
              rows={6}
              label="Message Body"
              {...register("body")}
              error={!!errors.body}
              helperText={errors.body?.message}
              variant="outlined"
              sx={{ "& .MuiOutlinedInput-root": { borderRadius: "12px" } }}
            />
          </Grid>

          <Grid item xs={12} sx={{ display: 'flex', gap: 2, mt: 3 }}>
            <Button
              fullWidth
              variant="outlined"
              onClick={handleCancel}
              sx={{
                py: 1.5,
                borderRadius: "12px",
                textTransform: "none",
                fontWeight: "700",
                borderColor: colors.redAccent[500],
                color: colors.redAccent[500],
                "&:hover": { borderColor: colors.redAccent[600], bgcolor: `${colors.redAccent[500]}0D` }
              }}
            >
              Cancel
            </Button>
            <Button
              fullWidth
              type="submit"
              variant="contained"
              endIcon={<Send size={18} />}
              sx={{
                py: 1.5,
                borderRadius: "12px",
                textTransform: "none",
                fontWeight: "700",
                bgcolor: colors.greenAccent[500],
                color: theme.palette.mode === "dark" ? colors.primary[500] : colors.primary[100],
                "&:hover": { bgcolor: colors.greenAccent[400], boxShadow: `0 8px 16px ${colors.greenAccent[500]}33` }
              }}
            >
              Transmit Receipt
            </Button>
          </Grid>
        </Grid>
      </form>
    </Box>
  );
};

ReceiptEmailingForm.propTypes = {
  receiptId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  emailAddress: PropTypes.string.isRequired,
  toggleModal: PropTypes.func,
};

export default ReceiptEmailingForm;
